import { lazy, Suspense, useEffect, useRef } from 'react'
import { BrowserRouter, Switch } from 'react-router-dom'
import {
    CompatRoute,
    CompatRouter,
    Navigate,
    useLocation,
    useNavigate,
} from 'react-router-dom-v5-compat'
import CssBaseline from '@mui/material/CssBaseline'
import { setUser } from '@sentry/react'
import AppBar from '~/components/AppBar'
import { JaymartSettings } from '~/integrations/jaymart/components/settings'
import { RainforestSettings, RainforestSettingsProduction, RainforestSettingsSandbox } from '~/integrations/rainforest/components/settings'
import AccessDeniedPage from '~/pages/access-denied'
import DashboardOverview from '~/pages/dashboard/overview'
import Reports from '~/pages/dashboard/reports'
import HomePage from '~/pages/home'
import ProductEditPage from '~/pages/product-edit'
import ProductCreatePage from '~/pages/product-new'
import Products from '~/pages/products.catalog'
import Discounts from '~/pages/promotions/discounts'
import GiftWithPurchaseEditPage from '~/pages/promotions/gift-with-purchase.[id].edit'
import GiftWithPurchaseCreatePage from '~/pages/promotions/gift-with-purchase.create'
import GiftWithPurchase from '~/pages/promotions/gift-with-purchase.index'
import ReturnDetailPage from '~/pages/returns.[id]'
import ReturnsPage from '~/pages/returns.index'
import { Billing } from '~/pages/settings.billing'
import { Groups } from '~/pages/settings.groups'
import { SettingsUsage } from '~/pages/settings.usage'
import { Users } from '~/pages/settings.users'
import ChannelStockBreakdownPage from '~/pages/stock/channel-stock-breakdown'
import StockManagerPage from '~/pages/stock/manager'
import { StoreSettings } from '~/settings/components/store-settings'
import { useAppSelector } from '~/store'
import { hasPermission } from '~/utils/hasPermission'
import { Conversations } from '../../conversations'
import Exports from '../../exports/containers/exports'
import PickPackBatch from '../../locations/containers/pick-pack-batch'
import PickPackBatches from '../../locations/containers/pick-pack-batches'
import ChannelInstructions from '../../platforms/containers/channel-instructions'
import ChannelPicker from '../../platforms/containers/channel-picker'
import ChannelResult from '../../platforms/containers/channel-result'
import Integrations from '../../platforms/containers/integrations'
import ProductImportIssues from '../../platforms/containers/product-import-issues'
import StorePicker from '../../platforms/containers/store-picker'
import MergeRecommendations from '../../products/containers/merge-recommendations'
import PriceManager from '../../products/containers/price-manager'
import StockReport from '../../products/containers/stock-report'
import Discount from '../../promotions/containers/discount'
import InitShipping from '../../sales/components/init-shipping'
import SellerFleetSales from '../../sales/components/seller-fleet'
import Sale from '../../sales/containers/sale'
import Sales from '../../sales/containers/sales'


import StockAdjustmentGroup from '../../stock-adjustment-groups/containers/group'
import StockAdjustmentGroups from '../../stock-adjustment-groups/containers/groups'
import OnboardingWizard from '../components/agent-onboarding-wizard'
import CreateNewStore from '../containers/create-new-store'
import Login from '../containers/login'
import LoginNew from '../containers/login-new'
import SignUp from '../containers/signup'
import { AppLayout } from './app-layout'

const ChatPage = lazy(() => import('~/pages/chat'))

export const PATHS = Object.freeze({
    dashboardOverview: '/home/dashboard/overview',
    dashboardReports: '/home/dashboard/reports',
    sales: '/home/sales/process',
    returns: '/home/sales/returns',
    returnDetail: '/home/sales/returns/:return_id',
    initShipping: '/home/sales/init-shipping',
    sale: '/home/sale/:sale_id',
    saleCreditNote: '/home/sale/:sale_id/credit-note',
    pickPackBatches: '/home/sales/pick-pack-batches',
    pickPackBatch: '/home/sales/pick-pack-batches/:pick_pack_batch_id',
    products: '/home/products/catalog',
    product: '/home/stores/:store_id/products/:product_id',
    mergeRecommendations: '/home/stores/:store_id/merge-recommendations',
    createProduct: '/home/products/create/stores/:store_id',
    priceManager: '/home/products/prices',

    discounts: '/home/promotions/discounts',
    discount: '/home/promotions/discounts/:discount_id',
    createDiscount: '/home/promotions/discounts/create/stores/:store_id',

    giftWithPurchaseList: '/home/promotions/gift-with-purchase',
    giftWithPurchaseCreate: '/home/promotions/gift-with-purchase/create',
    giftWithPurchaseEdit: '/home/promotions/gift-with-purchase/:campaign_id/edit',

    stockManager: '/home/stock/manager',
    stockReport: '/home/stock/report',
    stockAdjustmentGroups: '/home/stock/adjustment-groups',
    stockAdjustmentGroup: '/home/stock/adjustment-groups/:stock_adjustment_group_pk',
    createStockAdjustmentGroup: '/home/stock/adjustment-groups/create/stores/:store_id',
    channelStockItems: '/home/stock/channel-stock-items',
    channelStockBreakdown: '/home/stock/channel-stock-breakdown',

    addChannelSelectChannel: '/home/channels/add/',
    addChannelSelectStore: '/home/channels/:channel_id/add/stores/',
    addChannelInstructions: '/home/channels/:channel_id/stores/:store_id/add/instructions/',
    addChannelResult: '/home/channels/:channel_id/add/result',
    integrations: '/home/integrations',
    productImportIssues: '/home/shop/:shop_id/product-import-issues',
    settings: '/home/settings',
    settingsStores: '/home/settings/stores',
    settingsUsers: '/home/settings/users',
    settingsGroups: '/home/settings/groups',
    settingsBilling: '/home/settings/billing',
    settingsUsage: '/home/settings/usage',

    shopsDashboard: '/dashboard/shops',
    exports: '/home/sales/exports',

    // Jaymart
    jaymartSettings: '/home/jaymart/settings',
    // Rainforest
    rainforestSettingsProduction: '/home/rainforest/production/settings',
    rainforestSettingsSandbox: '/home/rainforest/sandbox/settings',

    chat: '/home/chat',
    conversations: '/home/conversations/conversations',

    sellerFleetSales: '/home/sales/seller-fleet',

    home: '/home',
    accessDenied: '/home/403',
})

/**
 * @deprecated use getPath from utils/index.js instead
 */
export function getPath(name) {
    const path = PATHS[name]
    return path.replace(/:/g, '')
}

function ScrollToTop() {
    const { pathname } = useLocation()

    useEffect(() => {
        if (pathname) {
            window.scrollTo(0, 0)
        }
    }, [pathname])

    return null
}

function LoggedInRoute({ children }) {
    const location = useLocation()
    const navigate = useNavigate()
    const user = useAppSelector((state) => state.initial.user)
    const redirectPath = useAppSelector((state) => state.initial.redirectPath)
    const loggedIn = useAppSelector((state) => state.login.loggedIn)

    const prevPath = useRef('')

    useEffect(() => {
        if (location.pathname === prevPath.current) {
            return
        }

        if (prevPath.current) {
            window.analytics.page({
                referrer: prevPath.current,
            })
        } else {
            window.analytics.page()
        }

        prevPath.current = location.pathname
    }, [location.pathname])

    useEffect(() => {
        if (!loggedIn || !user) {
            setUser(null)
            const pathname = `/login${redirectPath ? '/?redirect_path=' + redirectPath : ''}`
            navigate(pathname, { replace: true })
            return
        }

        setUser({ id: user.id, email: user.email })
    }, [loggedIn, user, redirectPath, navigate])

    if (!loggedIn || !user) {
        return null
    }

    return children
}

function RouteWithPermission({ component: Component, permission, render, ...rest }) {
    const userPermissions = useAppSelector((state) => state.initial.user.permissions)

    return (
        <CompatRoute
            {...rest}
            render={(props) => {
                if (permission && !hasPermission(userPermissions, permission)) {
                    return <Navigate replace to={PATHS.accessDenied} />
                }

                return Component ? <Component {...props} /> : render(props)
            }}
        />
    )
}

export default function App() {
    return (
        <Suspense fallback="">
            <BrowserRouter>
                <CompatRouter>
                    <ScrollToTop />
                    <CssBaseline />
                    <AppBar />
                    <Switch>
                        <CompatRoute path="/login-new">
                            <LoginNew />
                        </CompatRoute>
                        <CompatRoute path="/login">
                            <Login />
                        </CompatRoute>
                        <CompatRoute exact path="/signup">
                            <SignUp />
                        </CompatRoute>
                        <CompatRoute exact path="/signup/create-new-store">
                            <CreateNewStore />
                        </CompatRoute>
                        <CompatRoute path="/agent-onboarding">
                            <OnboardingWizard />
                        </CompatRoute>
                        <LoggedInRoute>
                            <AppLayout>
                                <CompatRoute path={PATHS.home} component={HomePage} exact />
                                <CompatRoute
                                    path={PATHS.accessDenied}
                                    component={AccessDeniedPage}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.analytics"
                                    path={PATHS.dashboardOverview}
                                    component={DashboardOverview}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.analytics"
                                    path={PATHS.dashboardReports}
                                    component={Reports}
                                    exact
                                />

                                <RouteWithPermission
                                    permission="module.sales"
                                    path={PATHS.sales}
                                    component={Sales}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.sales"
                                    path={PATHS.returns}
                                    component={ReturnsPage}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.sales"
                                    path={PATHS.returnDetail}
                                    component={ReturnDetailPage}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.sales"
                                    path={PATHS.sellerFleetSales}
                                    component={SellerFleetSales}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.sales"
                                    path={PATHS.exports}
                                    component={Exports}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.sales"
                                    path={PATHS.initShipping}
                                    component={InitShipping}
                                />
                                <RouteWithPermission
                                    permission="module.sales"
                                    path={PATHS.pickPackBatches}
                                    component={PickPackBatches}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.sales"
                                    path={PATHS.pickPackBatch}
                                    component={PickPackBatch}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.sales"
                                    path={PATHS.sale}
                                    component={Sale}
                                    exact
                                />

                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.products}
                                    component={Products}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.product}
                                    component={ProductEditPage}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.priceManager}
                                    component={PriceManager}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.createProduct}
                                    component={ProductCreatePage}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.mergeRecommendations}
                                    component={MergeRecommendations}
                                    exact
                                />

                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.discounts}
                                    component={Discounts}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.discount}
                                    component={Discount}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.createDiscount}
                                    component={Discount}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.giftWithPurchaseList}
                                    component={GiftWithPurchase}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.giftWithPurchaseCreate}
                                    component={GiftWithPurchaseCreatePage}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.products"
                                    path={PATHS.giftWithPurchaseEdit}
                                    component={GiftWithPurchaseEditPage}
                                    exact
                                />

                                <RouteWithPermission
                                    permission="module.stock"
                                    path={PATHS.stockManager}
                                    component={StockManagerPage}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.stock"
                                    path={PATHS.stockReport}
                                    component={StockReport}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.stock"
                                    path={PATHS.channelStockBreakdown}
                                    component={ChannelStockBreakdownPage}
                                    exact
                                />

                                <RouteWithPermission
                                    permission="module.stock"
                                    path={PATHS.stockAdjustmentGroups}
                                    component={StockAdjustmentGroups}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.stock"
                                    path={PATHS.stockAdjustmentGroup}
                                    component={StockAdjustmentGroup}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.stock"
                                    path={PATHS.createStockAdjustmentGroup}
                                    component={StockAdjustmentGroup}
                                    exact
                                />

                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.addChannelSelectStore}
                                    component={StorePicker}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.addChannelSelectChannel}
                                    component={ChannelPicker}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.addChannelInstructions}
                                    component={ChannelInstructions}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.addChannelResult}
                                    component={ChannelResult}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.integrations}
                                    component={Integrations}
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.productImportIssues}
                                    component={ProductImportIssues}
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.settingsStores}
                                    component={StoreSettings}
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.settingsUsers}
                                    component={Users}
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.settingsGroups}
                                    component={Groups}
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.settingsBilling}
                                    component={Billing}
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.settingsUsage}
                                    component={SettingsUsage}
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.jaymartSettings}
                                    component={JaymartSettings}
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.rainforestSettingsProduction}
                                    component={RainforestSettingsProduction}
                                />
                                <RouteWithPermission
                                    permission="module.settings"
                                    path={PATHS.rainforestSettingsSandbox}
                                    component={RainforestSettingsSandbox}
                                    exact
                                />
                                <RouteWithPermission
                                    permission="module.chat"
                                    path={PATHS.chat}
                                    render={() => (
                                        <Suspense fallback="">
                                            <ChatPage />
                                        </Suspense>
                                    )}
                                />
                                <RouteWithPermission
                                    permission="module.conversations"
                                    path={PATHS.conversations}
                                    component={Conversations}
                                    exact
                                />
                            </AppLayout>
                        </LoggedInRoute>
                    </Switch>
                </CompatRouter>
            </BrowserRouter>
        </Suspense>
    )
}
